import React, { useEffect, useState } from 'react';
import * as d3 from 'd3';
import TooltipText from '../../../Components/TooltipText';
import { Body } from '../../../Constants/Fonts';
import { formatNumber } from './_shared';

const TRANSPARENT_PX = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export function useWidth(ref) {
    const [width, setWidth] = useState(0);
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        setWidth(el.clientWidth);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(entries => setWidth(Math.round(entries[0].contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref]);
    return width;
}

export function niceMax(v) {
    const m = Math.max(1, v);
    const pow = 10 ** Math.floor(Math.log10(m));
    const n = m / pow;
    return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * pow;
}

function roundedTop(x, y, w, h, r = 4) {
    if (h <= 0) return '';
    const rr = Math.min(r, w / 2, h);
    return `M${x},${y + h} V${y + rr} Q${x},${y} ${x + rr},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h} Z`;
}

function Tip({ title, rows }) {
    return (
        <>
            <Body as="span" variant={2} weight="semibold" className="bm_chart_tip_date">{title}</Body>
            {rows.map(r => (
                <span key={r.label} className="bm_chart_tip_row">
                    {r.dotClass && <span className={`bm_chart_tip_dot ${r.dotClass}`} aria-hidden="true" />}
                    <Body as="span" variant={2} className="bm_chart_tip_label">{r.label}</Body>
                    <Body as="span" variant={2} weight="semibold" className="bm_chart_tip_val">{r.value}</Body>
                </span>
            ))}
        </>
    );
}

function PartLegend({ items }) {
    return (
        <ul className="bm_ov_legend">
            {items.map(s => (
                <li key={s.key} className="bm_ov_legend_item">
                    <span className={`bm_ov_swatch bm_ov_slot is-${s.slot}`} aria-hidden="true" />
                    <Body as="span" variant={2} className="bm_ov_legend_label">{s.label}</Body>
                    <Body as="span" variant={2} weight="semibold" className="bm_ov_legend_value">
                        {s.pct < 1 && s.value > 0 ? '<1' : s.pct}%
                    </Body>
                    <Body as="span" variant={2} className="bm_ov_legend_count">{formatNumber(s.value)}</Body>
                </li>
            ))}
        </ul>
    );
}

export function DonutChart({ slices, total, caption = 'mentions' }) {
    const size = 168;
    const r = size / 2;
    const arcs = d3.pie().value(s => s.value).sort(null).padAngle(slices.length > 1 ? 0.02 : 0)(slices);
    const arc = d3.arc().innerRadius(r * 0.64).outerRadius(r - 1).cornerRadius(3);
    return (
        <div className="bm_ov_part">
            <svg className="bm_ov_donut_svg" viewBox={`0 0 ${size} ${size}`}
                role="img" aria-label={slices.map(s => `${s.label} ${s.pct}%`).join(', ')}>
                <g transform={`translate(${r},${r})`}>
                    {arcs.map(a => <path key={a.data.key} d={arc(a)} className={`bm_ov_slot is-${a.data.slot}`} />)}
                </g>
                <text x={r} y={r + 2} textAnchor="middle" className="bm_ov_donut_total">{formatNumber(total)}</text>
                <text x={r} y={r + 20} textAnchor="middle" className="bm_ov_donut_caption">{caption}</text>
            </svg>
            <PartLegend items={slices} />
        </div>
    );
}

export function RankBars({ rows }) {
    const max = Math.max(1, ...rows.map(r => r.value));
    return (
        <ol className="bm_ov_rank">
            {rows.map(r => (
                <li key={r.key} className="bm_ov_rank_row">
                    <Body as="span" variant={2} className="bm_ov_rank_label">{r.label}</Body>
                    <span className="bm_ov_rank_track">
                        <span className="bm_ov_rank_fill" style={{ width: `${Math.max(2, (r.value / max) * 100)}%` }} />
                    </span>
                    <Body as="span" variant={2} weight="semibold" className="bm_ov_rank_pct">
                        {r.pct < 1 && r.value > 0 ? '<1' : r.pct}%
                    </Body>
                    <Body as="span" variant={2} className="bm_ov_rank_count">{formatNumber(r.value)}</Body>
                </li>
            ))}
        </ol>
    );
}

const SENT_KEYS = ['positive', 'neutral', 'negative'];
export function SentimentBars({ rows }) {
    const totalOf = r => (r.positive || 0) + (r.neutral || 0) + (r.negative || 0);
    return (
        <ul className="bm_ov_sbars">
            {rows.map(r => {
                const total = totalOf(r);
                return (
                    <li key={r.key}>
                        <div className="bm_ov_sbar_top">
                            <span className="bm_ov_sbar_who">
                                <Body as="span" variant={2} weight="semibold" className="bm_ov_sbar_name">{r.label}</Body>
                                <Body as="span" variant={2} className="bm_ov_sbar_sub">{r.sub}</Body>
                            </span>
                            {r.badge}
                            <Body as="span" variant={2} className="bm_ov_sbar_total">
                                <Body as="span" variant={2} weight="semibold" className="bm_ov_sbar_count">{formatNumber(total)}</Body>
                                {r.negative > 0 ? ` · ${formatNumber(r.negative)} neg` : ' · no negative'}
                            </Body>
                        </div>
                        <span className="bm_ov_sbar_track">
                            {SENT_KEYS.map(k => (r[k] || 0) > 0 && (
                                <span key={k} className={`bm_ov_sbar_seg is-${k}`} style={{ flexGrow: r[k] }} />
                            ))}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
}

export function ColumnChart({ bars, height = 190, ariaLabel }) {
    const ref = React.useRef(null);
    const W = useWidth(ref);
    const M = { top: 22, right: 6, bottom: 28, left: 6 };
    const iw = Math.max(0, W - M.left - M.right);
    const ih = height - M.top - M.bottom;
    const max = Math.max(1, ...bars.map(b => b.value));
    const band = bars.length ? iw / bars.length : iw;
    const colW = Math.min(24, band * 0.5);
    const base = M.top + ih;
    return (
        <div className="bm_chart_wrap" ref={ref}>
            {W > 0 && (
                <svg className="bm_chart_svg" width={W} height={height} role="img"
                    aria-label={ariaLabel || bars.map(b => `${b.label} ${b.display}`).join(', ')}>
                    {bars.map((b, i) => {
                        const cx = M.left + (i + 0.5) * band;
                        const h = (b.value / max) * ih;
                        return (
                            <g key={b.key}>
                                <path d={roundedTop(cx - colW / 2, base - h, colW, h)}
                                    className={`bm_ov_col${b.tone ? ` is-${b.tone}` : ''}`} />
                                <text className="bm_ov_cap" x={cx} y={base - h - 7} textAnchor="middle">{b.display}</text>
                                <text className="bm_ov_tick" x={cx} y={height - 8} textAnchor="middle">
                                    {b.label.length > 11 ? `${b.label.slice(0, 10)}…` : b.label}
                                </text>
                            </g>
                        );
                    })}
                    <line className="bm_ov_baseline" x1={M.left} x2={W - M.right} y1={base} y2={base} />
                </svg>
            )}
        </div>
    );
}

export function Histogram({ bins, height = 210, tickEvery = 3, ariaLabel }) {
    const ref = React.useRef(null);
    const W = useWidth(ref);
    const [hover, setHover] = useState(null);
    const M = { top: 22, right: 8, bottom: 26, left: 34 };
    const iw = Math.max(0, W - M.left - M.right);
    const ih = height - M.top - M.bottom;
    const top = niceMax(Math.max(1, ...bins.map(b => b.value)));
    const band = bins.length ? iw / bins.length : iw;
    const barW = Math.max(1, band - 2);
    const base = M.top + ih;
    const y = v => base - (v / top) * ih;
    const peak = bins.reduce((best, b, i) => (b.value > (bins[best]?.value || 0) ? i : best), 0);
    return (
        <div className="bm_chart_wrap" ref={ref}>
            {W > 0 && (
                <>
                    <svg className="bm_chart_svg" width={W} height={height} role="img" aria-label={ariaLabel}>
                        <g className="bm_ov_axis">
                            {[top / 2, top].map(t => (
                                <g key={t}>
                                    <line className="bm_ov_grid" x1={M.left} x2={W - M.right} y1={y(t)} y2={y(t)} />
                                    <text x={M.left - 8} y={y(t)} dy="0.32em" textAnchor="end">{formatNumber(t)}</text>
                                </g>
                            ))}
                            {bins.map((b, i) => (i % tickEvery === 0) && (
                                <text key={b.key} className="bm_ov_tick" x={M.left + i * band + band / 2} y={height - 6}
                                    textAnchor="middle">{b.label}</text>
                            ))}
                        </g>
                        {bins.map((b, i) => (
                            <path key={b.key}
                                d={roundedTop(M.left + i * band + 1, y(b.value), barW, base - y(b.value))}
                                className={`bm_ov_hist_bar${i === peak && b.value > 0 ? ' is-peak' : ''}${hover === i && b.value > 0 ? ' is-hover' : ''}${hover != null && hover !== i ? ' is-dim' : ''}`} />
                        ))}
                        {bins[peak] && bins[peak].value > 0 && (
                            <text className="bm_ov_cap" x={M.left + peak * band + band / 2} y={y(bins[peak].value) - 7}
                                textAnchor="middle">{formatNumber(bins[peak].value)}</text>
                        )}
                        <line className="bm_ov_baseline" x1={M.left} x2={W - M.right} y1={base} y2={base} />
                    </svg>
                    <div className="bm_chart_hits">
                        {bins.map((b, i) => (
                            <div key={b.key} className="bm_chart_hit is-column is-hist"
                                style={{ left: M.left + i * band, top: M.top, width: band, height: ih, '--bar-y': `${y(b.value) - M.top}px` }}
                                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                                <TooltipText src={TRANSPARENT_PX} alt=""
                                    className="bm_chart_tip"
                                    tooltipText={<Tip title={b.tip} rows={[{ label: 'Mentions', value: formatNumber(b.value), dotClass: 'is-mentions' }]} />} />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export function Sparkline({ points, width = 120, height = 34 }) {
    const n = points.length;
    if (n < 2) return <span className="bm_ov_spark_empty" aria-hidden="true" />;
    const max = Math.max(1, ...points.map(p => p.mentions || 0));
    const x = i => 2 + (i * (width - 8)) / (n - 1);
    const y = v => height - 3 - (v / max) * (height - 8);
    const line = d3.line().x((p, i) => x(i)).y(p => y(p.mentions || 0)).curve(d3.curveMonotoneX)(points);
    const area = d3.area().x((p, i) => x(i)).y0(height - 2).y1(p => y(p.mentions || 0)).curve(d3.curveMonotoneX)(points);
    const last = points[n - 1];
    return (
        <svg className="bm_ov_spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
            <path d={area} className="bm_ov_spark_area" />
            <path d={line} className="bm_ov_spark_line" />
            <circle cx={x(n - 1)} cy={y(last.mentions || 0)} r="3.5" className="bm_ov_spark_dot" />
        </svg>
    );
}

export function ShareLine({ points, height = 120, ariaLabel }) {
    const ref = React.useRef(null);
    const W = useWidth(ref);
    const [hover, setHover] = useState(null);
    const M = { top: 12, right: 40, bottom: 22, left: 34 };
    const iw = Math.max(0, W - M.left - M.right);
    const ih = height - M.top - M.bottom;
    const n = points.length;
    const top = Math.max(10, niceMax(Math.max(...points.map(p => p.value), 1)));
    const x = i => M.left + (n <= 1 ? iw / 2 : (i * iw) / (n - 1));
    const y = v => M.top + ih - (v / top) * ih;
    const line = d3.line().x((p, i) => x(i)).y(p => y(p.value)).curve(d3.curveMonotoneX)(points);
    const area = d3.area().x((p, i) => x(i)).y0(y(0)).y1(p => y(p.value)).curve(d3.curveMonotoneX)(points);
    const last = points[n - 1];
    const col = n ? iw / Math.max(1, n - 1) : iw;
    return (
        <div className="bm_chart_wrap" ref={ref}>
            {W > 0 && n > 0 && (
                <>
                    <svg className="bm_chart_svg" width={W} height={height} role="img" aria-label={ariaLabel}>
                        <g className="bm_ov_axis">
                            <line className="bm_ov_grid" x1={M.left} x2={W - M.right} y1={y(top)} y2={y(top)} />
                            <text x={M.left - 8} y={y(top)} dy="0.32em" textAnchor="end">{top}%</text>
                            <text x={M.left - 8} y={y(0)} dy="0.32em" textAnchor="end">0%</text>
                            <text className="bm_ov_tick" x={x(0)} y={height - 4} textAnchor="start">{points[0].label}</text>
                            {n > 1 && (
                                <text className="bm_ov_tick" x={x(n - 1)} y={height - 4} textAnchor="end">{last.label}</text>
                            )}
                        </g>
                        <line className="bm_ov_baseline" x1={M.left} x2={W - M.right} y1={y(0)} y2={y(0)} />
                        {n > 1 && <path d={area} className="bm_ov_share_area" />}
                        {n > 1 && <path d={line} className="bm_ov_share_line" />}
                        {hover != null && (
                            <circle cx={x(hover)} cy={y(points[hover].value)} r="4" className="bm_ov_share_dot" />
                        )}
                        <circle cx={x(n - 1)} cy={y(last.value)} r="4" className="bm_ov_share_dot" />
                        <text className="bm_ov_cap" x={x(n - 1) + 8} y={y(last.value)} dy="0.32em">{Math.round(last.value)}%</text>
                    </svg>
                    <div className="bm_chart_hits">
                        {points.map((p, i) => (
                            <div key={p.key} className="bm_chart_hit is-column"
                                style={{ left: x(i) - col / 2, top: M.top, width: col, height: ih }}
                                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                                <TooltipText src={TRANSPARENT_PX} alt=""
                                    className={`bm_chart_tip ${x(i) > W * 0.62 ? 'is-flip' : ''}`}
                                    tooltipText={<Tip title={p.tip} rows={[{ label: 'Negative share', value: `${Math.round(p.value)}%`, dotClass: 'is-negative is-bar' }]} />} />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export function RingGauge({ pct, label, size = 104, stroke = 9 }) {
    const has = pct != null && Number.isFinite(Number(pct));
    const v = has ? Math.max(0, Math.min(100, Number(pct))) : 0;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const tone = !has ? 'none' : v >= 90 ? 'good' : v >= 70 ? 'warn' : 'bad';
    return (
        <div className="bm_ov_ring">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
                aria-label={`${label}: ${has ? `${Math.round(v)}%` : 'no data'}`}>
                <circle cx={size / 2} cy={size / 2} r={r} className="bm_ov_ring_track" strokeWidth={stroke} />
                {has && v > 0 && (
                    <circle cx={size / 2} cy={size / 2} r={r} className={`bm_ov_ring_fill is-${tone}`}
                        strokeWidth={stroke} strokeDasharray={`${(v / 100) * c} ${c}`}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
                )}
                <text x={size / 2} y={size / 2} dy="0.35em" textAnchor="middle" className="bm_ov_ring_value">
                    {has ? `${Math.round(v)}%` : '-'}
                </text>
            </svg>
            <Body as="span" variant={2} className="bm_ov_ring_label">{label}</Body>
        </div>
    );
}
